import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { ArrowLeft, Loader2, MessageSquare, Send, Sparkles, Trash2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishTutor } from '../../hooks/useEnglishTutor'
import { cn } from '../../lib/utils'

// Suggested opener prompts when the conversation is empty. They post
// straight through (same as typing + send) so the user lands on a
// real assistant turn instead of staring at an empty composer.
// Fully-qualified i18n keys (the rest of the module nests under
// `english.*` — earlier these were `tutor.suggestions.*` which is
// a top-level path that doesn't exist, so the raw keys rendered).
const SUGGESTIONS = [
  'english.tutor.suggestions.roleplay',
  'english.tutor.suggestions.checkSentence',
  'english.tutor.suggestions.practiceTense',
  'english.tutor.suggestions.casualChat',
]

function MessageBubble({ message }) {
  const isUser = message.role === 'user'
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] sm:max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-app-primary-500/10 text-foreground rounded-ee-sm'
            : 'bg-muted/70 text-foreground rounded-es-sm',
        )}
        dir="auto"
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-strong:text-app-primary-500">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  )
}

export function EnglishTutorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const { messages, loading, sending, error, send, reset } = useEnglishTutor()
  const [draft, setDraft] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  // Pin scroll to the bottom whenever the message stream grows.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, sending])

  const handleSend = useCallback(async (textOverride) => {
    const text = (textOverride ?? draft).trim()
    if (!text || sending) return
    setDraft('')
    await send(text)
  }, [draft, sending, send])

  const handleReset = useCallback(async () => {
    if (!confirmReset) {
      setConfirmReset(true)
      // Auto-cancel after 3s so a missed confirm doesn't sit armed.
      setTimeout(() => setConfirmReset(false), 3000)
      return
    }
    setConfirmReset(false)
    await reset()
  }, [confirmReset, reset])

  const goBack = () => navigate('/english')

  return (
    <div
      className="flex flex-col content-fade pb-2"
      // 100dvh – top app chrome. The bottom tab bar auto-hides on
      // input focus now (useBottomBarVisibility), so we don't need
      // to reserve space for it — the composer sits at the natural
      // bottom of the page, and when the user focuses the input the
      // bar slides away on its own.
      style={{ height: 'calc(100dvh - 8rem)' }}
    >
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
            <MessageSquare className="w-4 h-4 text-app-primary-500" />
            {t('english.tutor.title')}
          </h1>
          <p className="text-xs text-muted-foreground" dir="auto">
            {t('english.tutor.subtitle')}
          </p>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleReset}
            aria-label={t('english.tutor.reset')}
            title={t('english.tutor.reset')}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              confirmReset
                ? 'text-red-500 bg-red-500/10'
                : 'text-muted-foreground hover:text-red-500 hover:bg-accent',
            )}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 pe-1"
      >
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : messages.length === 0 ? (
          <Card className="border-app-primary-500/30 bg-app-primary-500/5">
            <CardContent className="p-3 sm:p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground leading-relaxed" dir="auto">
                  {t('english.tutor.welcome')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSend(t(key))}
                    className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-accent text-foreground transition-colors"
                    dir="auto"
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          messages.map((m) => <MessageBubble key={m.id} message={m} />)
        )}
        {sending && (
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground py-2 px-3 rounded-2xl bg-muted/60" dir="auto">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>{t('english.tutor.thinking')}</span>
          </div>
        )}
        {error && (
          <div className="text-xs text-red-600 px-3 py-2 rounded-md bg-red-500/10 break-words" dir="auto">
            {error}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); handleSend() }}
        className="flex items-end gap-2 pt-3 mt-2 border-t border-border/60"
      >
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault()
              handleSend()
            }
          }}
          rows={2}
          placeholder={t('english.tutor.placeholder')}
          disabled={sending}
          className="flex-1 min-h-[44px] max-h-32 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground resize-none disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          aria-label={t('english.tutor.send')}
          className={cn(
            'h-11 w-11 rounded-lg inline-flex items-center justify-center transition-colors flex-shrink-0',
            draft.trim() && !sending
              ? 'bg-app-primary-500 text-white hover:bg-app-primary-500/90'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 rtl:-scale-x-100" />}
        </button>
      </form>
    </div>
  )
}
